<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <h1 class="text-h4 mb-4">{{ $t('dashboard.title') }}</h1>
      </v-col>
      <v-col cols="12" md="6" class="d-flex align-center justify-end flex-wrap ga-2">
        <v-select
          v-model="selectedCoinId"
          :items="coinFilterOptions"
          item-title="label"
          item-value="id"
          :label="$t('dashboard.filterByCoin')"
          density="compact"
          hide-details
          clearable
          style="max-width: 200px;"
        />
        <v-select
          v-model="selectedExchangeIds"
          :items="exchangeFilterOptions"
          item-title="name"
          item-value="id"
          :label="$t('dashboard.filterByExchange')"
          density="compact"
          hide-details
          clearable
          multiple
          chips
          style="max-width: 280px;"
        />
      </v-col>
    </v-row>

    <!-- Row 1: Exchanges - bars + coins pie -->
    <v-row>
      <v-col cols="12" md="6" style="height: 320px">
        <v-card height="100%">
          <v-card-title>{{ $t('dashboard.usdtByExchange') }}</v-card-title>
          <v-card-text style="height: 260px">
            <Bar v-if="exchangeChartData" :data="exchangeChartData" :options="barOptions" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" style="height: 320px">
        <v-card height="100%">
          <v-card-title>{{ formattedTotalUsdtExchangesPie }} ({{ $t('dashboard.exchangesOnly') }})</v-card-title>
          <v-card-text style="height: 260px">
            <Pie v-if="pieDataExchanges" :data="pieDataExchanges" :options="pieOptions" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- Row 2: Offices - bars + coins pie -->
    <v-row>
      <v-col cols="12" md="6" style="height: 320px">
        <v-card height="100%">
          <v-card-title>{{ $t('dashboard.usdtByOffices') }}</v-card-title>
          <v-card-text style="height: 260px">
            <Bar v-if="officesChartData" :data="officesChartData" :options="barOptions" />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" style="height: 320px">
        <v-card height="100%">
          <v-card-title>{{ formattedTotalUsdtOfficesPie }} ({{ $t('dashboard.officesOnly') }})</v-card-title>
          <v-card-text style="height: 260px">
            <Pie v-if="pieDataOffices" :data="pieDataOffices" :options="pieOptions" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- Row 3: Grand total -->
    <v-row>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title>{{ $t('dashboard.totalUsdt') }}</v-card-title>
          <v-card-text>
            <div class="text-h3 text-right">{{ formattedTotalUsdt }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title>{{ $t('dashboard.totalTodayProfit') }}</v-card-title>
          <v-card-text>
            <div class="text-h3 text-right">{{ formattedTotalTodayProfit }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title>{{ $t('dashboard.todayTotalExpenses') }}</v-card-title>
          <v-card-text>
            <div class="text-h3 text-right">{{ formattedTodayTotalExpenses }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { Bar, Pie } from 'vue-chartjs'
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ArcElement } from 'chart.js'
import { apiGet } from '../utils/api'
import { useI18n } from 'vue-i18n'

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, ArcElement)

// Plugin to add data labels on bar charts only (not on pie/doughnut)
const barDataLabelsPlugin = {
  id: 'barDataLabels',
  afterDatasetsDraw: (chart: any) => {
    if (chart.config.type !== 'bar') return
    const ctx = chart.ctx
    const locale = chart.options.locale || 'en-US'
    
    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      
      meta.data.forEach((bar: any, index: number) => {
        const value = dataset.data[index]
        if (value !== null && value !== undefined) {
          const formattedValue = new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value)
          
          ctx.save()
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.fillStyle = '#333'
          ctx.font = '12px Arial'
          
          const x = bar.x
          const y = bar.y
          ctx.fillText(formattedValue, x, y - 5)
          ctx.restore()
        }
      })
    })
  }
}

Chart.register(barDataLabelsPlugin)

const { t, locale } = useI18n()

const totalUsdt = ref(0)
const totalTodayProfit = ref(0)
const todayTotalExpenses = ref(0)
const selectedCoinId = ref<number | null>(null)
const selectedExchangeIds = ref<number[]>([])
const disabledCoinIds = ref<Set<number>>(new Set())

// Store raw data (declared before computeds that use them)
const allWallets = ref<any[]>([])
const allRates = ref<any[]>([])

// Coin filter dropdown options: All + coins that have non-zero total balance
const coinFilterOptions = computed(() => {
  const wallets = Array.isArray(allWallets.value) ? allWallets.value : []
  const balanceByCoin = new Map<number, number>()
  for (const w of wallets) {
    if (!w?.coin?.id) continue
    const balance = Number(w.balance ?? w.calculated_balance ?? 0)
    balanceByCoin.set(w.coin.id, (balanceByCoin.get(w.coin.id) ?? 0) + balance)
  }
  const coinsWithBalance = new Map<number, { id: number; symbol: string; name: string }>()
  for (const w of wallets) {
    if (w?.coin?.id && (balanceByCoin.get(w.coin.id) ?? 0) !== 0 && !coinsWithBalance.has(w.coin.id)) {
      coinsWithBalance.set(w.coin.id, { id: w.coin.id, symbol: String(w.coin.symbol ?? ''), name: String(w.coin.name ?? '') })
    }
  }
  const list = Array.from(coinsWithBalance.values()).sort((a, b) => (a.symbol || '').localeCompare(b.symbol || ''))
  return [{ id: null, label: t('dashboard.filterByCoinAll') }, ...list.map(c => ({ id: c.id, label: `${c.symbol} (${c.name})` }))]
})

// Clear header coin filter if selected coin no longer has non-zero balance
watch(coinFilterOptions, (opts) => {
  if (!Array.isArray(opts)) return
  const ids = new Set((opts as { id: number | null }[]).filter(o => o != null && o.id != null).map(o => o.id as number))
  if (selectedCoinId.value != null && !ids.has(selectedCoinId.value)) {
    selectedCoinId.value = null
  }
}, { deep: true })

// Exchange filter options: unique exchanges from wallets
const exchangeFilterOptions = computed(() => {
  const seen = new Map<number, { id: number; name: string }>()
  const wallets = Array.isArray(allWallets.value) ? allWallets.value : []
  wallets.forEach((w: any) => {
    if (w?.exchange?.id != null && !seen.has(w.exchange.id)) {
      seen.set(w.exchange.id, { id: w.exchange.id, name: w.exchange.name || t('dashboard.unknown') })
    }
  })
  return Array.from(seen.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
})


const formattedTotalUsdt = computed(() => {
  return new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(totalUsdt.value)
})

const formattedTotalTodayProfit = computed(() => {
  return new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(totalTodayProfit.value)
})

const formattedTodayTotalExpenses = computed(() => {
  return new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(todayTotalExpenses.value)
})

// Wallets after exchange + coin filter (before disabled coins)
const walletsAfterExchangeAndCoin = computed(() => {
  const list = Array.isArray(allWallets.value) ? allWallets.value : []
  let filtered = list
  if (selectedExchangeIds.value.length > 0) {
    const ids = new Set(selectedExchangeIds.value)
    filtered = filtered.filter((w: any) => w.exchange?.id != null && ids.has(w.exchange.id))
  }
  if (selectedCoinId.value !== null) {
    filtered = filtered.filter((w: any) => w.coin?.id === selectedCoinId.value)
  }
  return filtered
})

// Filter wallets: also exclude coins that are stroked out (disabled) in pie legend
const filteredWallets = computed(() => {
  const list = walletsAfterExchangeAndCoin.value
  if (disabledCoinIds.value.size === 0) return list
  return list.filter((w: any) => !disabledCoinIds.value.has(w.coin?.id))
})

// All coins for pie labels (so stroked-out coins stay in legend)
const walletsForPieLabels = computed(() => walletsAfterExchangeAndCoin.value)

// Exchange wallets only (office = false) and office wallets only (office = true), with same filter as filteredWallets
const filteredWalletsExchange = computed(() =>
  filteredWallets.value.filter((w: any) => !w.office)
)
const filteredWalletsOffices = computed(() =>
  filteredWallets.value.filter((w: any) => w.office)
)

// Create a map of coin_id to latest rate
const ratesMap = computed(() => {
  const map: Record<number, number> = {}
  const rates = Array.isArray(allRates.value) ? allRates.value : []
  rates.forEach((rate: any) => {
    if (rate.coin_id && rate.rate) {
      map[rate.coin_id] = Number(rate.rate)
    }
  })
  return map
})

// Calculate bar + byCoin for a given wallet list
const calculateChartDataForWallets = (wallets: any[]) => {
  const byExchange: Record<string, number> = {}
  const byOffices: Record<string, number> = {}
  const byCoin: Record<string, number> = {}
  wallets.forEach((w: any) => {
    const balance = Number(w.balance || w.calculated_balance || 0)
    const rate = ratesMap.value[w.coin?.id] || 0
    const v = balance * rate
    if (w.office) {
      const officeName = w.exchange?.name || t('dashboard.unknown')
      byOffices[officeName] = (byOffices[officeName] || 0) + v
    } else {
      const exchangeName = w.exchange?.name || t('dashboard.unknown')
      byExchange[exchangeName] = (byExchange[exchangeName] || 0) + v
    }
    const coinSymbol = w.coin?.symbol || t('dashboard.unknown')
    byCoin[coinSymbol] = (byCoin[coinSymbol] || 0) + v
  })
  return { byExchange, byOffices, byCoin }
}

const exchangeChartData = computed(() => {
  const { byExchange } = calculateChartDataForWallets(filteredWalletsExchange.value)
  return {
    labels: Object.keys(byExchange),
    datasets: [{
      label: t('dashboard.usdt'),
      data: Object.values(byExchange),
      backgroundColor: '#3f51b5'
    }]
  }
})

const officesChartData = computed(() => {
  const { byOffices } = calculateChartDataForWallets(filteredWalletsOffices.value)
  return {
    labels: Object.keys(byOffices),
    datasets: [{
      label: t('dashboard.usdt'),
      data: Object.values(byOffices),
      backgroundColor: '#66bb6a'
    }]
  }
})

const PIE_COLORS = [
  '#42a5f5', '#66bb6a', '#ffa726', '#ab47bc', '#ef5350', '#26a69a',
  '#ec407a', '#5c6bc0', '#78909c', '#8d6e63', '#7e57c2', '#ef9a9a',
  '#a5d6a7', '#90caf9', '#ce93d8', '#ffcc80', '#80cbc4', '#b39ddb',
  '#ffab91', '#b2dfdb', '#d7ccc8', '#c5e1a5', '#ffe082', '#9fa8da'
]

function buildPieDataFromWallets(wallets: any[], walletsForLabels?: any[]) {
  const byCoin: Record<string, number> = {}
  wallets.forEach((w: any) => {
    const balance = Number(w.balance || w.calculated_balance || 0)
    const rate = ratesMap.value[w.coin?.id] || 0
    const v = balance * rate
    const symbol = w.coin?.symbol || t('dashboard.unknown')
    byCoin[symbol] = (byCoin[symbol] || 0) + v
  })
  // When some coins are disabled (stroked out), keep them in legend with value 0 in pie – only non-zero balance coins
  if (disabledCoinIds.value.size > 0 && walletsForLabels && walletsForLabels.length > 0) {
    const byCoinAll: Record<string, number> = {}
    const symbolToCoinId: Record<string, number> = {}
    walletsForLabels.forEach((w: any) => {
      const balance = Number(w.balance || w.calculated_balance || 0)
      const rate = ratesMap.value[w.coin?.id] || 0
      const v = balance * rate
      const symbol = w.coin?.symbol || t('dashboard.unknown')
      byCoinAll[symbol] = (byCoinAll[symbol] || 0) + v
      if (w.coin?.id) symbolToCoinId[symbol] = w.coin.id
    })
    const labels = Object.keys(byCoinAll).filter((s) => Number(byCoinAll[s] || 0) !== 0)
    const realValues = labels.map((s) => byCoinAll[s] || 0)
    const data = labels.map((s) =>
      disabledCoinIds.value.has(symbolToCoinId[s]) ? 0 : (byCoinAll[s] || 0)
    )
    const colors = labels.map((_, i) => PIE_COLORS[i % PIE_COLORS.length])
    return {
      labels,
      datasets: [{ label: t('dashboard.usdt'), data, backgroundColor: colors, realValues }]
    }
  }
  // Only include coins with non-zero balance (pie and legend)
  const entries = Object.entries(byCoin).filter(([, val]) => Number(val) !== 0)
  const labels = entries.map(([s]) => s)
  const data = entries.map(([, v]) => v)
  return {
    labels,
    datasets: [{ label: t('dashboard.usdt'), data, backgroundColor: PIE_COLORS, realValues: data }]
  }
}

const walletsForPieLabelsExchanges = computed(() =>
  walletsForPieLabels.value.filter((w: any) => !w.office)
)
const walletsForPieLabelsOffices = computed(() =>
  walletsForPieLabels.value.filter((w: any) => w.office)
)

const pieDataExchanges = computed(() =>
  buildPieDataFromWallets(filteredWalletsExchange.value, walletsForPieLabelsExchanges.value)
)
const pieDataOffices = computed(() =>
  buildPieDataFromWallets(filteredWalletsOffices.value, walletsForPieLabelsOffices.value)
)

// Total USDT for active coins in each pie (for card title)
const totalUsdtExchangesPie = computed(() => {
  const ds = pieDataExchanges.value?.datasets?.[0]
  if (!ds?.data?.length) return 0
  return (ds.data as number[]).reduce((s, v) => s + Number(v || 0), 0)
})
const totalUsdtOfficesPie = computed(() => {
  const ds = pieDataOffices.value?.datasets?.[0]
  if (!ds?.data?.length) return 0
  return (ds.data as number[]).reduce((s, v) => s + Number(v || 0), 0)
})
const formattedTotalUsdtExchangesPie = computed(() =>
  new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(totalUsdtExchangesPie.value)
)
const formattedTotalUsdtOfficesPie = computed(() =>
  new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(totalUsdtOfficesPie.value)
)

// Chart options with click handlers
const barOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  locale: locale.value,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      enabled: true,
      callbacks: {
        label: (context: any) => {
      //    const value = context.parsed.y
      //     return new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
      //       style: 'currency',
      //       currency: 'USD',
      //       minimumFractionDigits: 2,
      //       maximumFractionDigits: 2
      //     }).format(value)
        }
      }
    }
  }
}))

function toggleCoinDisabled(coinId: number) {
  if (disabledCoinIds.value.has(coinId)) {
    disabledCoinIds.value.delete(coinId)
  } else {
    disabledCoinIds.value.add(coinId)
  }
  selectedCoinId.value = null
  disabledCoinIds.value = new Set(disabledCoinIds.value)
}

const pieOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  onClick: (event: any, elements: any[]) => {
    if (elements.length > 0) {
      const chart = event.chart
      const label = chart.data.labels?.[elements[0].index]
      const wallet = allWallets.value.find((w: any) => w.coin?.symbol === label)
      if (wallet?.coin) toggleCoinDisabled(wallet.coin.id)
    }
  },
  onHover: (event: any, elements: any[]) => {
    event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default'
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        generateLabels: (chart: any) => {
          const ds = chart.data.datasets?.[0]
          const amountFmt = (val: number) => new Intl.NumberFormat(locale.value === 'ru' ? 'ru-RU' : 'en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(val)
          return (chart.data.labels ?? []).map((label: string, i: number) => {
            const wallet = allWallets.value.find((w: any) => w.coin?.symbol === label)
            const coinId = wallet?.coin?.id
            const disabled = coinId != null && disabledCoinIds.value.has(coinId)
            const value = Number(ds?.realValues?.[i] ?? ds?.data?.[i] ?? 0)
            const amountStr = amountFmt(value)
            const namePart = disabled ? Array.from(label).join('\u0336') + '\u0336' : label
            const displayText = `${namePart} ${amountStr}`
            return {
              text: displayText,
              fillStyle: disabled ? '#9e9e9e' : (ds?.backgroundColor?.[i] ?? '#888'),
              strokeStyle: disabled ? '#9e9e9e' : (ds?.backgroundColor?.[i] ?? '#888'),
              lineWidth: 1,
              hidden: false,
              index: i
            }
          })
        }
      },
      onClick: (_e: any, legendItem: any, legend: any) => {
        if (legend?.chart) {
          const rawLabel = legend.chart.data.labels?.[legendItem.index]
          const wallet = allWallets.value.find((w: any) => w.coin?.symbol === rawLabel)
          if (wallet?.coin) toggleCoinDisabled(wallet.coin.id)
        }
      }
    },
    tooltip: { enabled: false },
    datalabels: false
  }
}))

const loadDashboardData = async () => {
  try {
    const response = await apiGet('/api/dashboard')
    if (response.ok) {
      const data = await response.json()
      totalUsdt.value = data.total_usdt || 0
      totalTodayProfit.value = data.total_today_profit || 0
      todayTotalExpenses.value = data.total_today_expenses || 0
    }
  } catch (error) {
    console.error('Dashboard error:', error)
  }
}

const loadWalletsAndRates = async () => {
  try {
    const [walletsRes, ratesRes] = await Promise.all([
      apiGet('/api/wallets'),
      apiGet('/api/coin_rates?latest=true')
    ])
    
    if (walletsRes.ok && ratesRes.ok) {
      allWallets.value = await walletsRes.json()
      allRates.value = await ratesRes.json()
    }
  } catch (error) {
    console.error('Load wallets/rates error:', error)
  }
}

onMounted(async () => {
  await loadDashboardData()
  await loadWalletsAndRates()
}
)
</script>
